/**
 * ルート予約（docs/06_ui-ux.md 4 章）。
 * 編集していないときは sim の予約の写しを持ち、編集中は仮の予約（staged）を持つ。確定すると sim へ setPlan を送る。
 */
import { LAUNCH, PACK } from '../config/balance';
import type { EdgeData, NodeData } from '../core/world';
import { edgeTimeS, type HikeModifiers } from '../sim/athlete/hike';
import type { AthleteState, PlanStep } from '../sim/types';
import type { Arc, Graph } from '../sim/world/graph';
import { findPath } from '../sim/world/pathfind';

export type PlanItem = { kind: 'hike'; to: number } | { kind: 'flight'; to: number };

export interface PreviewLeg {
  kind: 'hike' | 'flight';
  from: number;
  to: number;
  arcs: Arc[];
  /** 今からの開始時刻（秒） */
  startS: number;
  durationS: number;
  lengthM: number;
  ascentM: number;
  descentM: number;
  /** フライト：準備・飛行・格納の内訳と、滑空だけで届くか */
  prepS: number;
  flightS: number;
  packS: number;
  reachable: boolean;
  /** 届かない理由：far＝高さが足りない、ridge＝途中の尾根を越えられない */
  blocked: 'far' | 'ridge' | null;
}

export interface PlanPreview {
  legs: PreviewLeg[];
  /** 行けない地点（歩いて行けない、離陸地ではない、など） */
  error: string | null;
  totalS: number;
}

/** 平均の対地速度（滑空だけ／サーマルを使う）。予約の見積もり用 */
const GLIDE_SPEED_MS = 10;
const XC_SPEED_MS = 7;

export function itemsToSteps(items: readonly PlanItem[]): PlanStep[] {
  return items.map((i) =>
    i.kind === 'hike' ? { kind: 'hike', to: i.to } : { kind: 'flight', targetNodeId: i.to },
  );
}

/** sim の選手の予約を、起点ノードから見た PlanItem の列にする */
export function itemsFromAthlete(a: AthleteState, anchorNode: number | null): PlanItem[] {
  const steps: PlanStep[] = [...(a.current ? [a.current] : []), ...a.plan];
  const items: PlanItem[] = [];
  for (const s of steps) {
    if (s.kind === 'hike') {
      if (s.to !== anchorNode || items.length > 0) items.push({ kind: 'hike', to: s.to });
    } else items.push({ kind: 'flight', to: s.targetNodeId });
  }
  return items;
}

export class Planner {
  private live: PlanItem[] = [];
  private staged: PlanItem[] | null = null;
  awaitingFlightTarget = false;
  origin = 0;
  originEtaS = 0;
  private cache: { key: string; preview: PlanPreview } | null = null;

  constructor(
    private readonly graph: Graph,
    private readonly mods: () => HikeModifiers,
    private readonly glideRatio: () => number,
    /** 地形の高さ（フライトが途中の尾根を越えられるかの判定用。省略時は判定しない） */
    private readonly heightAt: ((x: number, z: number) => number) | null = null,
  ) {}

  /** 滑空だけで届くか。届かなければその理由（高さが足りない／途中の尾根を越えられない） */
  private glideReaches(from: NodeData, to: NodeData): 'far' | 'ridge' | null {
    const dist = Math.hypot(to.pos.x - from.pos.x, to.pos.z - from.pos.z);
    const ratio = this.glideRatio();
    let blocked: 'far' | 'ridge' | null =
      dist > Math.max(0, from.pos.y - to.pos.y - 150) * ratio ? 'far' : null;
    if (this.heightAt) {
      for (let k = 1; k < 12; k++) {
        const d = (dist * k) / 12;
        const x = from.pos.x + ((to.pos.x - from.pos.x) * k) / 12;
        const z = from.pos.z + ((to.pos.z - from.pos.z) * k) / 12;
        const ground = this.heightAt(x, z);
        if (from.pos.y - d / ratio < ground + 80) {
          // 目標より高い地形にぶつかるなら「尾根」、そうでなければ単に遠すぎる
          blocked = ground > to.pos.y + 200 ? 'ridge' : (blocked ?? 'far');
          break;
        }
      }
    }
    return blocked;
  }

  get editing(): boolean {
    return this.staged !== null;
  }

  get items(): readonly PlanItem[] {
    return this.staged ?? this.live;
  }

  /** 編集していないときに、sim の予約と起点を写す */
  syncFromSim(items: PlanItem[], origin: number | null, etaS: number): void {
    if (this.editing) return;
    this.live = items;
    if (origin !== null) this.origin = origin;
    this.originEtaS = etaS;
  }

  endNode(): number {
    const last = this.items[this.items.length - 1];
    return last ? last.to : this.origin;
  }

  canFlyFromEnd(): boolean {
    return !!this.graph.nodes[this.endNode()]?.launch && !this.awaitingFlightTarget;
  }

  beginEdit(): void {
    if (!this.staged) this.staged = [...this.live];
  }

  /** 最後の地点（離陸地）から飛ぶ。次のクリックが目標になる */
  startFlight(): boolean {
    if (!this.canFlyFromEnd()) return false;
    this.beginEdit();
    this.awaitingFlightTarget = true;
    return true;
  }

  clickNode(id: number, mode: 'extend' | 'replace'): void {
    this.beginEdit();
    const s = this.staged as PlanItem[];
    if (this.awaitingFlightTarget) {
      if (id !== this.endNode()) s.push({ kind: 'flight', to: id });
      this.awaitingFlightTarget = false;
      return;
    }
    if (mode === 'replace') {
      this.staged = id === this.origin ? [] : [{ kind: 'hike', to: id }];
      return;
    }
    if (id !== this.endNode()) s.push({ kind: 'hike', to: id });
  }

  undo(): void {
    if (this.awaitingFlightTarget) {
      this.awaitingFlightTarget = false;
      return;
    }
    this.staged?.pop();
  }

  clear(): void {
    this.beginEdit();
    this.staged = [];
    this.awaitingFlightTarget = false;
  }

  /** 確定できたら sim に送るステップを返す */
  confirm(): PlanStep[] | null {
    if (!this.staged) return itemsToSteps(this.live);
    if (this.preview().error) return null;
    this.live = this.staged;
    this.staged = null;
    this.awaitingFlightTarget = false;
    return itemsToSteps(this.live);
  }

  cancel(): void {
    this.staged = null;
    this.awaitingFlightTarget = false;
  }

  preview(): PlanPreview {
    const key = `${this.origin}:${Math.round(this.originEtaS / 60)}:${JSON.stringify(this.items)}`;
    if (this.cache?.key === key) return this.cache.preview;
    const mods = this.mods();
    const cost = (e: EdgeData, fwd: boolean): number => edgeTimeS(e, fwd, mods);
    const legs: PreviewLeg[] = [];
    let cur = this.origin;
    let t = this.originEtaS;
    let error: string | null = null;
    for (const item of this.items) {
      const from = this.graph.nodes[cur] as NodeData;
      const to = this.graph.nodes[item.to] as NodeData;
      if (item.kind === 'hike') {
        const path = findPath(this.graph, cur, item.to, cost);
        if (!path) {
          error = `${to.name ?? '選んだ地点'}へは歩いて行けません`;
          break;
        }
        let lengthM = 0;
        let ascentM = 0;
        let descentM = 0;
        for (const arc of path.arcs) {
          const e = this.graph.edges[arc.edgeId] as EdgeData;
          lengthM += e.lengthM;
          ascentM += arc.forward ? e.ascentM : e.descentM;
          descentM += arc.forward ? e.descentM : e.ascentM;
        }
        legs.push({
          kind: 'hike',
          from: cur,
          to: item.to,
          arcs: path.arcs,
          startS: t,
          durationS: path.costS,
          lengthM,
          ascentM,
          descentM,
          prepS: 0,
          flightS: 0,
          packS: 0,
          reachable: true,
          blocked: null,
        });
        t += path.costS;
      } else {
        if (!from.launch) {
          error = 'フライトは離陸地（▲）からしか予約できません';
          break;
        }
        const dist = Math.hypot(to.pos.x - from.pos.x, to.pos.z - from.pos.z);
        const blocked = this.glideReaches(from, to);
        const reachable = blocked === null;
        const flightS = dist / (reachable ? GLIDE_SPEED_MS : XC_SPEED_MS);
        legs.push({
          kind: 'flight',
          from: cur,
          to: item.to,
          arcs: [],
          startS: t,
          durationS: LAUNCH.prepS + flightS + PACK.normalS,
          lengthM: dist,
          ascentM: 0,
          descentM: Math.max(0, from.pos.y - to.pos.y),
          prepS: LAUNCH.prepS,
          flightS,
          packS: PACK.normalS,
          reachable,
          blocked,
        });
        t += LAUNCH.prepS + flightS + PACK.normalS;
      }
      cur = item.to;
    }
    const preview = { legs, error, totalS: t - this.originEtaS };
    this.cache = { key, preview };
    return preview;
  }

  /** 最後の地点から滑空だけで届く距離（届く範囲の円に使う） */
  glideRangeFromEnd(): number {
    const n = this.graph.nodes[this.endNode()] as NodeData;
    return Math.max(0, n.pos.y - 900) * this.glideRatio();
  }
}
